import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Typography, Collapse, Box } from '@mui/material';


/*
 * WeatherCard — now uses the SELECTED DRONE OBJECT from DroneSelector.
 * - Accepts `drone` as EITHER a full object (preferred) or a legacy key string.
 * - Re-evaluates instantly when the drone changes (no re-fetch).
 * - Keeps Tomorrow.io primary, WeatherAPI fallback.
 * - Keeps sun/moon/alerts notes, and shows a Selected Drone block built from the object.
 */

const STATUS_MESSAGES = {
  go: '✅ Clear for Takeoff',
  advisory: 'ℹ️ Clear With Advisories',
  caution: '⚠️ Flight Not Recommended',
  'no-go': '❌ Do Not Fly',
  unknown: 'ℹ️ Clear With Advisories'
};

// API keys (kept)
const tomorrow = { a: "Duz3", b: "59QJsgMP53YVt", c: "xLaM6AZVYaIgLZf" };
const TOMORROW_API_KEY = tomorrow.c + tomorrow.b + tomorrow.a;
const WEATHERAPI_KEY = process.env.REACT_APP_WEATHERAPI_KEY || (() => {
  const a="806", b="bbff941c3b3f4131a991844532508", c="06";
  return b + a + c;
})();

// --- helpers ---
const msToMph = (m) => m * 2.23694;
const msToKmh = (m) => m * 3.6;
const asNumber = (v) => {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  // strip units if present, e.g. "25 mph" -> 25
  const m = String(v).match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : NaN;
};
const asBool = (v) => {
  if (typeof v === 'boolean') return v;
  const s = String(v || '').trim().toLowerCase();
  return s === 'yes' || s === 'true' || s === '1';
};

// severity → icon
function iconFor(sev) {
  return sev === 'go' ? '✅' : sev === 'advisory' ? 'ℹ️' : sev === 'caution' ? '⚠️' : '❌';
}
function windSeverity(mph, maxWind) {
  if (!isFinite(maxWind) || maxWind <= 0) return 'advisory';
  if (mph > maxWind) return 'no-go';
  if (mph > maxWind * 0.75) return 'caution';
  return 'go';
}
function dewSpreadSeverity(spreadC) {
  if (spreadC < 1) return 'no-go';
  if (spreadC < 2) return 'caution';
  if (spreadC < 4) return 'advisory';
  return 'go';
}

// Legacy built-ins (used only if a legacy string key is passed)
const LEGACY_MAP = {
  phantom:  { name: "DJI Phantom (all)",  maxWind: 22, canFlyInRain: false, maxDistance: 7,   maxFlightTime: 30 },
  matrice:  { name: "DJI Matrice (all)",  maxWind: 39, canFlyInRain: true,  maxDistance: 9.3, maxFlightTime: 55 },
  mavic:    { name: "DJI Mavic (all)",    maxWind: 23, canFlyInRain: false, maxDistance: 7.5, maxFlightTime: 34 },
  air3:     { name: "DJI Air 3",          maxWind: 25, canFlyInRain: false, maxDistance: 12.4, maxFlightTime: 46 },
  mini3pro: { name: "DJI Mini 3 Pro",     maxWind: 24, canFlyInRain: false, maxDistance: 7.5, maxFlightTime: 34 }
};

// Convert whatever we receive in `drone` into a normalized spec object
function normalizeDroneSpecs(drone) {
  // Preferred: full object from DroneSelector.js
  if (drone && typeof drone === 'object') {
    const name = drone.name || 'Custom Drone';
    const maxWind = asNumber(drone.maxWindTolerance ?? drone.maxWind);
    const canFlyInRain = asBool(drone.canFlyInRain ?? false);
    const maxDistance = asNumber(drone.maxRange);
    const maxFlightTime = asNumber(drone.maxFlightTime);

    // Sane defaults if any are missing
    return {
      name,
      maxWind: isFinite(maxWind) ? maxWind : 22,
      canFlyInRain,
      maxDistance: isFinite(maxDistance) ? maxDistance : undefined,
      maxFlightTime: isFinite(maxFlightTime) ? maxFlightTime : undefined
    };
  }

  // Legacy: a string key like "mavic"
  if (typeof drone === 'string' && LEGACY_MAP[drone]) {
    return LEGACY_MAP[drone];
  }

  // Fallback
  return LEGACY_MAP['phantom'];
}

const WeatherCard = ({ lat, lon, drone, refreshTrigger, onUpdate }) => {
  const [summaryText, setSummaryText] = useState('Weather data not loaded.');
  const [detailHtml, setDetailHtml] = useState('');
  const [notesStaticHtml, setNotesStaticHtml] = useState(''); // sun/moon/alerts
  const [specsHtml, setSpecsHtml] = useState(''); // selected drone block
  const [severity, setSeverity] = useState('unknown');
  const [expanded, setExpanded] = useState(false);
  const [weatherValues, setWeatherValues] = useState(null); // Tomorrow.io values or distilled fallback
  const [kpData, setKpData] = useState(null);

  // Normalized, memoized drone specs
  const currentSpecs = useMemo(() => normalizeDroneSpecs(drone), [drone]);

  // Build the Selected Drone block when specs change
  useEffect(() => {
    const parts = [
      `<div style="margin-top:6px;"><strong>Selected Drone:</strong> ${currentSpecs.name}`
    ];
    if (currentSpecs.maxWind != null)
      parts.push(`<br/><strong>Max Wind:</strong> ${currentSpecs.maxWind} mph`);
    parts.push(`<br/><strong>Can Fly in Rain:</strong> ${currentSpecs.canFlyInRain ? 'Yes' : 'No'}`);
    if (currentSpecs.maxDistance != null)
      parts.push(`<br/><strong>Max Range:</strong> ${currentSpecs.maxDistance} mi`);
    if (currentSpecs.maxFlightTime != null)
      parts.push(`<br/><strong>Max Flight Time:</strong> ${currentSpecs.maxFlightTime} min`);
    parts.push(`</div>`);
    setSpecsHtml(parts.join(''));
  }, [currentSpecs]);

  // Evaluate overall severity and build detail section from Tomorrow.io "values"
  const evaluateConditionsTomorrow = (c) => {
    const issues = [];
    const wind_mph = msToMph(c.windSpeed || 0);
    const gust_mph = msToMph(c.windGust || 0);
    const vis_miles = (c.visibility || 0) * 0.621371;
    const humidity = c.humidity;
    const weatherCode = c.weatherCode;

    // wind vs drone limit
    const wSev = windSeverity(wind_mph, currentSpecs.maxWind);
    const gSev = windSeverity(gust_mph, currentSpecs.maxWind);
    if (wSev === 'no-go') issues.push('❌ Wind too strong, flight not permitted.');
    else if (wSev === 'caution') issues.push('⚠️ High wind near limit.');

    if (gSev === 'no-go') issues.push('❌ Gusts too strong, flight not permitted.');
    else if (gSev === 'caution') issues.push('⚠️ High wind gusts near limit.');

    if (vis_miles < 3) issues.push('❌ Visibility below minimum.');
    else if (vis_miles < 5) issues.push('⚠️ Visibility limited.');

    if (humidity > 95) issues.push('⚠️ High humidity/fog; reduced visibility.');

    // Tomorrow.io weatherCode buckets
    if ([2000, 2100, 4000, 4200].includes(weatherCode)) {
      issues.push('⚠️ Light precipitation detected.');
    } else if ([4001, 4201].includes(weatherCode)) {
      // only block if drone cannot fly in rain
      issues.push(currentSpecs.canFlyInRain ? '⚠️ Heavy rain detected.' : '❌ Heavy rain detected; drone not rain-capable.');
    } else if ([5000, 5001, 5100, 5101].includes(weatherCode)) {
      issues.push('❌ Snow/ice conditions detected.');
    } else if (weatherCode >= 7000 && weatherCode < 8000) {
      issues.push('⚠️ Visibility hazard (fog, smoke, etc.).');
    }

    let sev = 'go';
    if (issues.some(m => m.startsWith('❌'))) sev = 'no-go';
    else if (issues.some(m => m.startsWith('⚠️'))) sev = 'caution';
    else if (issues.length > 0) sev = 'advisory';

    return { sev, issues };
  };

  function buildFlightConditionsDetail(c, lat, lon, dataTimeISO) {
    const windMph = msToMph(c.windSpeed || 0);
    const windKmh = msToKmh(c.windSpeed || 0);
    const gustMph = msToMph(c.windGust || 0);
    const gustKmh = msToKmh(c.windGust || 0);
    const ceilingFt = c.cloudCeiling ? Math.round(c.cloudCeiling * 3280.84) : Infinity;
    const dewSpread = (c.temperature - c.dewPoint);
    const dewSev = dewSpreadSeverity(dewSpread);
    const windSev = windSeverity(windMph, currentSpecs.maxWind);
    const gustSev = windSeverity(gustMph, currentSpecs.maxWind);
    const ceilIcon = ceilingFt === Infinity ? 'ℹ️' : '✅';

    let html = `<strong>Flight conditions at:</strong> ${lat.toFixed(4)}, ${lon.toFixed(4)}<br/>`;
    html += `${iconFor(windSev)} Wind: ${Math.round(windMph)} mph (${Math.round(windKmh)} km/h)<br/>`;
    html += `${iconFor(gustSev)} Gusts: ${Math.round(gustMph)} mph (${Math.round(gustKmh)} km/h)<br/>`;
    html += `${ceilIcon} Ceiling: ${ceilingFt === Infinity ? 'unlimited' : ceilingFt + ' ft'}<br/>`;
    html += `${iconFor(dewSev)} Dew-point spread: ${dewSpread.toFixed(1)}°C<br/>`;
    html += `<hr/><strong>Additional info:</strong><br/>`;
    html += `Temp: ${c.temperature}°C, Humidity: ${c.humidity}%, UV Index: ${c.uvIndex}<br/>`;
    html += `Cloud Cover: ${c.cloudCover}%<br/>`;
    html += `<small>Updated: ${new Date(dataTimeISO).toLocaleString()}</small><br/>`;
    return html;
  }

  // Fetch notes (sun/moon/alerts). Kept separate so drone changes don’t re-fetch.
  useEffect(() => {
    const fetchNotes = async () => {
      if (!lat || !lon) {
        setNotesStaticHtml('');
        return;
      }
      try {
		  console.debug('🔁 WeatherCard running fetch with:', { lat, lon, refreshTrigger });

        const sunRes = await fetch(`https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lon}&formatted=0`);
        const sunData = await sunRes.json();
        let sunHtml = '';
        if (sunData.results) {
          const toLocalTime = (isoStr) =>
            new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const { sunrise, sunset, civil_twilight_begin: civB, civil_twilight_end: civE } = sunData.results;
          const nowLocal = new Date();
          const pastCivilTwilight = civE ? nowLocal >= new Date(civE) : false;
          const civilIcon = pastCivilTwilight ? '❗' : '✅';
          sunHtml =
            `<div><strong>Sunrise:</strong> ${toLocalTime(sunrise)} | <strong>Sunset:</strong> ${toLocalTime(sunset)}<br/>` +
            ` ${civilIcon} <strong>Civil Twilight:</strong> ${toLocalTime(civB)} – ${toLocalTime(civE)}</div>` +
            (pastCivilTwilight
              ? `<div style="color:#d32f2f;font-weight:600;margin-top:4px;">! Anti‑collision lighting required.</div>`
              : '');
        }

        let moonHtml = '';
        try {
          const moonRes = await fetch('https://wttr.in/Moon?format=%m+%l');
          const moonText = await moonRes.text();
          if (moonText) moonHtml = `<div><strong>Moon:</strong> ${moonText.trim()}</div>`;
        } catch { /* ignore */ }

        let alertsHtml = '';
        try {
          const alertsRes = await fetch(`https://api.weather.gov/alerts/active?point=${lat},${lon}`);
          const alertsData = await alertsRes.json();
          if (alertsData.features?.length) {
            const alert = alertsData.features[0].properties;
            alertsHtml = `<div><strong>Weather Alert:</strong> ${alert.event}<br/>${alert.headline}</div>`;
          }
        } catch { /* ignore */ }

        setNotesStaticHtml(`${sunHtml}${moonHtml}${alertsHtml}`);
      } catch {
        setNotesStaticHtml('');
      }
    };
    fetchNotes();
  }, [lat, lon]);

  // Main fetch (Tomorrow primary; WeatherAPI fallback)
  useEffect(() => {
    const fetchWeather = async () => {
      if (!lat || !lon) {
        setSeverity('advisory');
        setSummaryText(STATUS_MESSAGES['advisory']);
        setDetailHtml('No location set. Click "Use My Location" or enter an address.');
        onUpdate && onUpdate('advisory', ['No location set']);
        setWeatherValues(null);
        return;
      }

      try {
        setSummaryText('Getting local weather...');
        setDetailHtml('');
        setSeverity('unknown');

const [weatherRes, kpRes] = await Promise.all([
  fetch(`https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&apikey=${TOMORROW_API_KEY}`),
  fetch('https://flightiq.app/API/kpdata.json?t=' + Date.now())
]);

const weatherData = await weatherRes.json();
const kpData = await kpRes.json();

if (!weatherRes.ok || !weatherData.data) throw new Error('Tomorrow API error');
if (!kpRes.ok || kpData.kp === undefined) throw new Error('Kp Index API error');

const c = weatherData.data.values;
setWeatherValues(c);
setKpData(kpData); // Save the Kp data

// First, evaluate the conditions to get 'sev' and 'issues'
const { sev, issues } = evaluateConditionsTomorrow(c, kpData);

// NOW you can use 'sev' to build the summary
const summary = STATUS_MESSAGES[sev] || STATUS_MESSAGES['unknown'];
const detail = buildFlightConditionsDetail(c, lat, lon, weatherData.data.time);

setSummaryText(summary);
        setDetailHtml(detail + (issues.length
          ? `<br/><span style="color:orange;">${issues.join('<br/>')}</span>`
          : `<br/><span style="color:green;">${STATUS_MESSAGES['go']}</span>`));
        setSeverity(sev);
        onUpdate && onUpdate(sev, issues);
      } catch (err) {
        // Fallback: WeatherAPI
		console.error('⛈️ Tomorrow.io failed:', err);
        try {
          const url2 = `https://api.weatherapi.com/v1/current.json?key=${WEATHERAPI_KEY}&q=${lat},${lon}`;
          const res2 = await fetch(url2);
          const data2 = await res2.json();
          if (!res2.ok || !data2.current) throw new Error('WeatherAPI error');

          const c2 = data2.current;
          // Make a minimal "values" object so re-eval path is shared
          const cCompat = {
            windSpeed: (c2.wind_mph || 0) / 2.23694,
            windGust: (c2.gust_mph || 0) / 2.23694,
            visibility: (c2.vis_km || 0) / 1.60934, // km -> miles -> back to km? Keep direct in text below
            humidity: c2.humidity,
            temperature: c2.temp_c,
            dewPoint: (c2.dewpoint_c ?? c2.temp_c), // if not provided
            cloudCeiling: undefined,
            uvIndex: c2.uv ?? 0,
            cloudCover: c2.cloud ?? 0,
            weatherCode: undefined
          };

          // Reuse wind vs maxWind logic
          const windSev = windSeverity(c2.wind_mph || 0, currentSpecs.maxWind);
          const gustSev = windSeverity(c2.gust_mph || 0, currentSpecs.maxWind);

          const issues = [];
          if (windSev === 'no-go') issues.push('❌ Wind too strong, flight not permitted.');
          else if (windSev === 'caution') issues.push('⚠️ High wind near limit.');
          if (gustSev === 'no-go') issues.push('❌ Gusts too strong, flight not permitted.');
          else if (gustSev === 'caution') issues.push('⚠️ High wind gusts near limit.');
          if ((c2.vis_miles || 0) < 3) issues.push('❌ Visibility below minimum.');
          else if ((c2.vis_miles || 0) < 5) issues.push('⚠️ Visibility limited.');
          if ((c2.humidity || 0) > 95) issues.push('⚠️ High humidity/fog; reduced visibility.');
          if (/rain|snow|storm/i.test(c2.condition?.text || '') && !currentSpecs.canFlyInRain) {
            issues.push(`❌ ${c2.condition.text} detected; this drone cannot fly in such conditions.`);
          }

          let sev = 'go';
          if (issues.some(m => m.startsWith('❌'))) sev = 'no-go';
          else if (issues.some(m => m.startsWith('⚠️'))) sev = 'caution';
          else if (issues.length > 0) sev = 'advisory';

          const summary = STATUS_MESSAGES[sev] || STATUS_MESSAGES['unknown'];
          let detail = `<strong>Flight conditions at:</strong> ${lat.toFixed(4)}, ${lon.toFixed(4)}<br/>`;
          detail += `${iconFor(windSev)} Wind: ${Math.round(c2.wind_mph || 0)} mph (${Math.round(c2.wind_kph || 0)} km/h)<br/>`;
          detail += `${iconFor(gustSev)} Gusts: ${Math.round(c2.gust_mph || 0)} mph (${Math.round(c2.gust_kph || 0)} km/h)<br/>`;
          detail += `ℹ️ Visibility: ${c2.vis_miles ?? '?'} miles (${c2.vis_km ?? '?'} km)<br/>`;
          detail += `<hr/><strong>Additional info:</strong><br/>`;
          detail += `Temp: ${c2.temp_c}°C, Humidity: ${c2.humidity}%, UV Index: ${c2.uv ?? '—'}<br/>`;
          detail += `Cloud Cover: ${c2.cloud ?? '—'}%<br/>`;
          detail += `<small>Updated: ${c2.last_updated}</small><br/>`;

          setWeatherValues(cCompat);
          setSummaryText(summary);
          setDetailHtml(detail + (issues.length
            ? `<br/><span style="color:orange;">${issues.join('<br/>')}</span>`
            : `<br/><span style="color:green;">${STATUS_MESSAGES['go']}</span>`));
          setSeverity(sev);
          onUpdate && onUpdate(sev, issues);
} catch (err) {
  console.error('🌩️ Weather fetch failed entirely:', err);
  setSeverity('advisory');
  setSummaryText(STATUS_MESSAGES['advisory']);
  setDetailHtml('Unable to retrieve weather data...');
  if (onUpdate) {
    console.debug('WeatherCard: calling onUpdate with advisory');
    onUpdate('advisory', ['Weather data unavailable']);
  }
  setWeatherValues(null);
}

      }
    };
    fetchWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, refreshTrigger]);

  // Re-evaluate SEVERITY + DETAIL immediately when the drone changes (no re-fetch).
  useEffect(() => {
    if (!weatherValues) return;
    const { sev, issues } = evaluateConditionsTomorrow(weatherValues);
    const summary = STATUS_MESSAGES[sev] || STATUS_MESSAGES['unknown'];

    // Try to reconstruct a detail block that reflects the new limits.
    // If Tomorrow values had time, use now as fallback.
    const timeISO = new Date().toISOString();
    const detail = buildFlightConditionsDetail(
      // weatherValues is in Tomorrow "values" shape for both providers
      weatherValues,
      lat, lon,
      timeISO
    );

    setSummaryText(summary);
    setDetailHtml(detail + (issues.length
      ? `<br/><span style="color:orange;">${issues.join('<br/>')}</span>`
      : `<br/><span style="color:green;">${STATUS_MESSAGES['go']}</span>`));
    setSeverity(sev);
    onUpdate && onUpdate(sev, issues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentSpecs)]); // triggers when selected drone changes

  const borderColor = severity === 'go' ? 'green'
                    : severity === 'caution' ? 'orange'
                    : severity === 'no-go' ? 'red'
                    : 'gray';

  return (
    <Card sx={{ borderLeft: `6px solid ${borderColor}`, mb: 1 }} onClick={() => setExpanded(!expanded)}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Weather</Typography>
          <Typography variant="body1" sx={{ opacity: 0.7 }}>⛅</Typography>
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: summaryText }} />

        <Collapse in={expanded}>
          {/* Notes (sun/moon/alerts) + Selected Drone block */}
          <Typography variant="body2" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: notesStaticHtml + specsHtml }} />
          {/* Detail section */}
          <Typography variant="body2" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: detailHtml }} />
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default WeatherCard;
